import { type ReactNode } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { ShieldX, Loader2 } from "lucide-react";

interface PermissionGuardProps {
  /** Single permission key (granular like "finance.view" or coarse like "finance") */
  permission?: string;
  /** Multiple permission keys — user needs ANY of them */
  anyOf?: string[];
  /** Multiple permission keys — user needs ALL of them */
  allOf?: string[];
  /** Content to render when permission is granted */
  children: ReactNode;
  /** Optional fallback to render instead of default access denied */
  fallback?: ReactNode;
}

export function PermissionGuard({
  permission,
  anyOf,
  allOf,
  children,
  fallback,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } =
    usePermissions();

  if (isLoading) return <GuardLoading />;

  let allowed = false;

  if (permission) {
    allowed = hasPermission(permission);
  } else if (anyOf && anyOf.length > 0) {
    allowed = hasAnyPermission(...anyOf);
  } else if (allOf && allOf.length > 0) {
    allowed = hasAllPermissions(...allOf);
  } else {
    // No permission specified — allow access
    allowed = true;
  }

  if (!allowed) return fallback ? <>{fallback}</> : <AccessDenied />;

  return <>{children}</>;
}

/** The page a guard shows the caller it refuses (PermissionGuard, KycComplianceGate). */
export function AccessDenied() {
  const { t } = useLanguage();
  return (
    <div
      data-testid="access-denied"
      className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground"
    >
      <ShieldX className="h-16 w-16 opacity-50" />
      <h2 className="text-xl font-semibold">
        {t("غير مصرح بالوصول", "Access Denied")}
      </h2>
      <p className="text-sm">
        {t(
          "ليس لديك صلاحية للوصول لهذه الصفحة. تواصل مع المسؤول.",
          "You don't have permission to access this page. Contact your administrator."
        )}
      </p>
    </div>
  );
}

/** The spinner a guard shows while it decides. */
export function GuardLoading() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
